// components/AdminSupport/SupportDetails.jsx
import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, Paperclip } from "lucide-react";
import { get } from "jquery";
import { getSupportTicketDetails, supportReply } from "../../actions/user";

const badgeClass = (priority) =>
  priority === "high" ? "danger" : priority === "medium" ? "warning" : "secondary";

const SupportDetails = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);

  const scrollerRef = useRef(null);
  const attachmentUrlRef = useRef(null);

  const scrollToBottom = () => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      // Simulate network delay
      const data = await getSupportTicketDetails(ticketId);
      // console.log(data);
      

      const dummy = {
        status: "success",
        message: "Support ticket retrieved successfully.",
        data: {
          support_ticket: {
            id: ticketId,
            title: "I can't login in to my account",
            description:
              "Please i can't seems to be able to login to my cardplot account can you help me resolved it. Thanks!",
            priority: "high",
            attachment:
              "https://cardplot.test/storage/support_attachments/fc69IUAiENI8m8nOwzT8GDptDhSS9T7z9l2yfAvZ.png",
            status: "open",
            attended_at: null,
            resolved_at: null,
            replies: [
              {
                id: "019895f5-8dbc-708f-9e85-c748373d74ba",
                message: "We will get back to you soon",
                attachment: null,
                sender_type: "admin",
                sender_name: "Support",
                created_at: "2025-08-10T21:49:14.000000Z",
              },
              {
                id: "019895f5-8dbc-708f-9e85-user",
                message: "Thanks, I'm waiting for feedback.",
                attachment: null,
                sender_type: "user",
                sender_name: "User",
                created_at: "2025-08-10T21:50:14.000000Z",
              },
            ],
            replies_count: 2,
            created_at: "2025-08-10T19:31:42.000000Z",
            updated_at: "2025-08-10T19:31:42.000000Z",
          },
        },
      };

      const tick = data.data.support_ticket;
      setTicket(tick);
      setMessages(tick.replies || []);
      setTimeout(scrollToBottom, 50);
    } catch (err) {
      // console.error(err);
      toast.error("Failed to load ticket details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    return () => {
      if (attachmentUrlRef.current) {
        URL.revokeObjectURL(attachmentUrlRef.current);
        attachmentUrlRef.current = null;
      }
    };
  }, [ticketId]);

  const submitReply = async (e) => {
    e.preventDefault();

    if (!message.trim()) {
      toast.error("Type a message and add an attachment.");
      return;
    }

    setPosting(true);
    try {
      // console.log(attachment);
      
      const data = await supportReply(ticketId, message, attachment);
      // console.log(data);

      let previewUrl = null;
      if (attachment) {
        // create a temporary preview URL to mimic uploaded attachment URL
        previewUrl = URL.createObjectURL(attachment);
        // keep a reference to revoke later
        if (attachmentUrlRef.current) URL.revokeObjectURL(attachmentUrlRef.current);
        attachmentUrlRef.current = previewUrl;
      }

      // Mimic reply payload from your reply endpoint
      const newReply = {
        id: `${Date.now()}`,
        message,
        attachment: previewUrl,
        sender_type: "user",
        sender_name: "User",
        created_at: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, newReply]);
      setMessage("");
      setAttachment(null);
      toast.success("Reply sent");
      setTimeout(scrollToBottom, 20);
    } catch (err) {
      const fallback =
        err?.response?.data?.errors?.message[0] ||
        err?.response?.data?.errors?.attachment[0] ||
          err?.response?.data?.message ||
          "Failed to send reply";
        toast.error(fallback);
      // console.error(err);
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <button className="btn btn-link mb-3" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} /> Back
        </button>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="container">
        <button className="btn btn-link mb-3" onClick={() => navigate(-1)}>
          <ArrowLeft size={18} /> Back
        </button>
        <div className="alert alert-warning">Ticket not found.</div>
      </div>
    );
  }

  return (
    <div className="container">
      <button className="btn btn-link mb-3" onClick={() => navigate(-1)}>
        <ArrowLeft size={18} /> Back
      </button>

      {/* Header card */}
      <div className="card shadow-sm mb-3">
        <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
          <div>
            <h5 className="mb-1">{ticket.title}</h5>
            <small className="text-muted">
              Created: {ticket.created_at ? new Date(ticket.created_at).toLocaleString() : "—"}
            </small>
            <p className="mb-0 mt-2 text-muted">{ticket.description}</p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <span className={`badge bg-${badgeClass(ticket.priority)}`}>{ticket.priority?.toUpperCase()}</span>
            <span
              className={`badge ${
                ticket.status === "open" ? "bg-success" : ticket.status === "resolved" ? "bg-secondary" : "bg-dark"
              }`}
            >
              {ticket.status || "open"}
            </span>
          </div>
        </div>
        {ticket.attachment && (
          <div className="card-footer">
            <a href={ticket.attachment} target="_blank" rel="noreferrer">
              View ticket attachment
            </a>
          </div>
        )}
      </div>

      {/* Chat thread */}
      <div className="card shadow-sm">
        <div className="card-header">
          <strong>Conversation</strong>
        </div>

        <div
          ref={scrollerRef}
          className="card-body"
          style={{ maxHeight: 420, overflowY: "auto", background: "#fafafa" }}
        >
          {messages.length === 0 ? (
            <div className="text-center text-muted">No messages yet.</div>
          ) : (
            messages.map((m) => (
              <div
                key={m.id || m.created_at}
                className={`d-flex mb-3 ${
                  (m.sender_type || m.sender) === "admin" ? "justify-content-start" : "justify-content-end"
                }`}
              >
                <div
                  className={`p-2 rounded ${
                    (m.sender_type || m.sender) === "admin" ? "bg-primary text-white" : "bg-white border"
                  }`}
                  style={{ maxWidth: "75%" }}
                >
                  <div className="small mb-1">
                    <strong>{(m.sender_type || m.sender) === "admin" ? "Support" : "You"}</strong>{" "}
                    <span className="text-light-emphasis small">
                      · {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                    </span>
                  </div>
                  <div>{m.message}</div>
                  {m.attachment && (
                    <div className="mt-2">
                      <a
                        className={(m.sender_type || m.sender) === "admin" ? "text-white" : ""}
                        href={m.attachment}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View attachment
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Composer */}
        <div className="card-footer">
          <form onSubmit={submitReply} className="d-flex flex-column flex-md-row gap-2">
            <textarea
              className="form-control"
              placeholder="Type your message…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            ></textarea>
            <div className="d-flex align-items-center">
              <input
                type="file"
                className="d-none"
                onChange={(e) => setAttachment(e.target.files[0])}
                id="attachment-input"
              />
            </div>
            <div className="d-flex align-items-end gap-2 mt-2 mt-md-0">
              <label htmlFor="attachment-input" className="p-2" role="button" title="Add attachment">
                <Paperclip size={18} />
              </label>
              <button className="btn btn-primary rounded-pill" type="submit" disabled={posting}>
                <small>
                  {posting ? <span className="spinner-border spinner-border-sm" role="status" /> : "Send"}
                </small>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SupportDetails;